import React, { useMemo, useState } from 'react';
import { ArrowLeftIcon, CameraIcon, CheckCircle2Icon, ClockIcon, FileIcon, ImageIcon, PackageIcon, PlayIcon, PlusIcon, ReceiptIcon, Trash2Icon } from 'lucide-react';
import { Employee, JobStatus, Material, ProJob, TimeEntry, WorkType } from '../../types/pro';
import { JOB_STATUS, formatDate, formatTime, hoursOnJob, kr, newLineId, toISODate } from '../../utils/pro';
import { StatusChip } from '../../components/pro/StatusChip';
import { EmptyState } from '../../components/pro/EmptyState';
import { TimeEntryList } from '../../components/pro/TimeEntryList';
interface ProJobDetailProps {
  job: ProJob;
  employees: Employee[];
  materials: Material[];
  timeEntries: TimeEntry[];
  companyHourlyRate: number;
  onBack: () => void;
  onUpdate: (changes: Partial<ProJob>) => void;
  onAddTime: (entry: Omit<TimeEntry, 'id'>) => void;
  onUpdateTime: (id: string, changes: Partial<TimeEntry>) => void;
  onRemoveTime: (id: string) => void;
  onCreateInvoice: (job: ProJob) => void;
}
type Tab = 'overblik' | 'timer' | 'materialer' | 'billeder' | 'dokumenter' | 'noter';
const TABS: {
  id: Tab;
  label: string;
}[] = [{
  id: 'overblik',
  label: 'Overblik'
}, {
  id: 'timer',
  label: 'Timer'
}, {
  id: 'materialer',
  label: 'Materialer'
}, {
  id: 'billeder',
  label: 'Billeder'
}, {
  id: 'dokumenter',
  label: 'Dokumenter'
}, {
  id: 'noter',
  label: 'Noter'
}];
const WORK_TYPES: WorkType[] = ['Montage', 'Fejlfinding', 'Service', 'Kørsel', 'Andet'];
export function ProJobDetail({
  job,
  employees,
  materials,
  timeEntries,
  companyHourlyRate,
  onBack,
  onUpdate,
  onAddTime,
  onUpdateTime,
  onRemoveTime,
  onCreateInvoice
}: ProJobDetailProps) {
  const [tab, setTab] = useState<Tab>('overblik');
  const [timeForm, setTimeForm] = useState({
    employeeId: employees[0]?.id ?? '',
    hours: '',
    workType: 'Montage' as WorkType,
    note: ''
  });
  const [materialPick, setMaterialPick] = useState('');
  const [materialQty, setMaterialQty] = useState('1');
  const own = useMemo(() => timeEntries.filter(entry => entry.jobId === job.id), [timeEntries, job.id]);
  const registered = hoursOnJob(timeEntries, job.id ?? '');
  const overrun = job.estimatedHours > 0 && registered > job.estimatedHours;
  const materialTotal = job.materials.reduce((sum, item) => sum + item.quantity * item.salesPrice, 0);
  const usesOwnRate = typeof job.hourlyRate === 'number';
  const rate = usesOwnRate ? job.hourlyRate as number : companyHourlyRate;
  // Older jobs have no billing type saved; they are all regningsarbejde.
  const billing = job.billingType ?? 'regning';
  const labourTotal = registered * rate;
  const setStatus = (status: JobStatus) => onUpdate({
    status
  });
  const addTime = () => {
    const hours = parseFloat(timeForm.hours.replace(',', '.'));
    if (!hours || !timeForm.employeeId || !job.id) return;
    onAddTime({
      employeeId: timeForm.employeeId,
      jobId: job.id,
      workType: timeForm.workType,
      date: toISODate(new Date()),
      hours,
      note: timeForm.note.trim(),
      billable: timeForm.workType !== 'Kørsel'
    });
    setTimeForm({
      ...timeForm,
      hours: '',
      note: ''
    });
  };
  const addMaterial = () => {
    const material = materials.find(m => m.id === materialPick);
    const quantity = parseFloat(materialQty.replace(',', '.'));
    if (!material || !quantity) return;
    onUpdate({
      materials: [...job.materials, {
        id: newLineId(),
        materialId: material.id,
        name: material.name,
        itemNo: material.itemNo,
        quantity,
        costPrice: material.costPrice,
        salesPrice: material.salesPrice
      }]
    });
    setMaterialPick('');
    setMaterialQty('1');
  };
  const card = 'rounded-xl border border-stone-200 bg-white';
  const field = 'w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500';
  const actions = <>
      {job.status !== 'i-gang' && job.status !== 'faerdig' && <button onClick={() => setStatus('i-gang')} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-stone-900 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-stone-800" data-id="element-1583" data-mp="1fcdc5jrhrzql">
          <PlayIcon className="h-4 w-4" data-id="element-1584" data-mp="1t3rstjxxfouh" />
          Start arbejde
        </button>}
      <button onClick={() => setTab('timer')} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-stone-200 bg-white px-4 py-3 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-50" data-id="element-1585" data-mp="1y232nuo81zcp">
        <ClockIcon className="h-4 w-4" data-id="element-1586" data-mp="27289a2llctpt" />
        Registrer tid
      </button>
      <button onClick={() => setTab('materialer')} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-stone-200 bg-white px-4 py-3 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-50" data-id="element-1587" data-mp="8oq4yoam79kk">
        <PackageIcon className="h-4 w-4" data-id="element-1588" data-mp="e90ljnvo0pe" />
        Tilføj materiale
      </button>
      {job.status !== 'faerdig' && job.status !== 'faktureret' ? <button onClick={() => setStatus('faerdig')} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-700" data-id="element-1589" data-mp="3rlbpg4g8tuwg">
          <CheckCircle2Icon className="h-4 w-4" data-id="element-1590" data-mp="j50gl42depcr" />
          Afslut opgave
        </button> : job.status === 'faerdig' && <button onClick={() => onCreateInvoice(job)} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-1591" data-mp="f1u3pdkr5zxb">
            <ReceiptIcon className="h-4 w-4" data-id="element-1592" data-mp="2uujipxf79f7c" />
            Opret faktura
          </button>}
    </>;
  return <div className="pb-24 lg:pb-0" data-id="element-1593" data-mp="2wchwbq3reux2">
      <button onClick={onBack} className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-stone-500 transition-colors hover:text-stone-900" data-id="element-1594" data-mp="31b1j1sq6gaqh">
        <ArrowLeftIcon className="h-4 w-4" data-id="element-1595" data-mp="2ndd0irjl1fol" />
        Alle opgaver
      </button>

      <header className="mb-5 flex flex-wrap items-start justify-between gap-4" data-id="element-1596" data-mp="2rbaq3vh9m3si">
        <div className="min-w-0" data-id="element-1597" data-mp="2dkflivdsu8uq">
          <div className="flex flex-wrap items-center gap-2.5" data-id="element-1598" data-mp="2dje1w2ipwe1e">
            <p className="font-mono text-xs font-semibold text-brand-700" data-id="element-1599" data-mp="2icz1u03vfi38">
              {job.jobNo}
            </p>
            <StatusChip meta={JOB_STATUS[job.status]} data-id="element-1600" data-mp="23ovrkeispk8m" />
          </div>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-stone-900 sm:text-3xl" data-id="element-1601" data-mp="28nfi6s8c3k7b">
            {job.title}
          </h1>
          <p className="mt-1 text-sm text-stone-500" data-id="element-1602" data-mp="1unegjak8w8qs">
            {job.customerName} · {job.address}, {job.city}
          </p>
        </div>
        <label className="flex-shrink-0" data-id="element-1603" data-mp="1fznua6zm7kmu">
          <span className="sr-only" data-id="element-1604" data-mp="1g0pasqzegzby">Skift status</span>
          <select value={job.status} onChange={e => setStatus(e.target.value as JobStatus)} className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm font-medium outline-none focus:ring-2 focus:ring-brand-500" data-id="element-1605" data-mp="1b12w0d3xbb11">
            {(Object.keys(JOB_STATUS) as JobStatus[]).map(status => <option key={status} value={status} data-id="element-1606" data-mp="1yx795r8r7xh6">
                {JOB_STATUS[status].label}
              </option>)}
          </select>
        </label>
      </header>

      {/* Estimated vs registered, side by side while the job is still running */}
      <section className={`${card} mb-5 grid grid-cols-2 gap-px overflow-hidden bg-stone-200 sm:grid-cols-4`} data-id="element-1607" data-mp="23oyrjehraww8">
        {[{
        label: 'Estimeret',
        value: `${job.estimatedHours} t`
      }, {
        label: 'Registreret',
        value: `${registered.toLocaleString('da-DK')} t`,
        alert: overrun
      }, {
        label: 'Materialer',
        value: kr(materialTotal)
      }, billing === 'fast-pris' ? {
        label: 'Fast pris',
        value: kr(job.fixedPrice ?? 0),
        hint: `Forbrug: ${kr(labourTotal + materialTotal)}`
      } : {
        label: 'Arbejdsløn',
        value: kr(labourTotal),
        hint: `${rate.toLocaleString('da-DK')} kr/t${usesOwnRate ? ' · egen pris' : ''}`
      }].map(stat => <div key={stat.label} className="bg-white p-4" data-id="element-1608" data-mp="3ackisxjq7soz">
            <p className={`text-[11px] font-semibold uppercase tracking-wider ${stat.alert ? 'text-amber-600' : 'text-stone-400'}`} data-id="element-1609" data-mp="3aazpfb95efsp">
              {stat.label}
            </p>
            <p className={`mt-1 text-lg font-bold tabular-nums ${stat.alert ? 'text-amber-600' : 'text-stone-900'}`} data-id="element-1610" data-mp="2mmgip05qyb2q">
              {stat.value}
            </p>
            {stat.hint && <p className={`mt-0.5 text-xs ${usesOwnRate ? 'font-medium text-brand-700' : 'text-stone-400'}`} data-id="element-1611" data-mp="2a2jiomcxbxjz">
                {stat.hint}
              </p>}
          </div>)}
      </section>

      <div role="tablist" aria-label="Opgavesektioner" className="mb-5 flex gap-1 overflow-x-auto rounded-xl border border-stone-200 bg-white p-1" data-id="element-1612" data-mp="1w6ft56czwddu">
        {TABS.map(item => <button key={item.id} role="tab" aria-selected={tab === item.id} onClick={() => setTab(item.id)} className={`whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-medium transition-colors ${tab === item.id ? 'bg-brand-50 text-brand-700' : 'text-stone-500 hover:text-stone-900'}`} data-id="element-1613" data-mp="1mfbde4fhwohy">
            {item.label}
          </button>)}
      </div>

      {tab === 'overblik' && <div className="space-y-5" data-id="element-1614" data-mp="2q73jjvge5jol">
          <section className={`${card} p-5`} data-id="element-1615" data-mp="34y3uc74m1pnw">
            <h2 className="mb-3 text-base font-semibold text-stone-900" data-id="element-1616" data-mp="34zx0llbba7vd">
              Beskrivelse
            </h2>
            <textarea value={job.description} onChange={e => onUpdate({
          description: e.target.value
        })} rows={4} className={field} data-id="element-1617" data-mp="2c96nd05unfag" />
          </section>

          <section className={`${card} p-5`} data-id="element-1618" data-mp="2m7vwa3frppek">
            <h2 className="mb-4 text-base font-semibold text-stone-900" data-id="element-1619" data-mp="2m8nrhd5gdl4b">
              Planlægning
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-id="element-1620" data-mp="2ham9kvjrjruf">
              <label className="block" data-id="element-1621" data-mp="1fkanszzzs4p">
                <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-1622" data-mp="3sneze0szu67u">
                  Start
                </span>
                <input type="datetime-local" value={job.start ? job.start.slice(0, 16) : ''} onChange={e => onUpdate({
              start: e.target.value ? new Date(e.target.value).toISOString() : undefined,
              status: job.status === 'ikke-planlagt' && e.target.value ? 'planlagt' : job.status
            })} className={field} data-id="element-1623" data-mp="41a66nfpxg2c" />
              </label>
              <label className="block" data-id="element-1624" data-mp="dzhao6stm5ng">
                <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-1625" data-mp="i12vcebljo87">
                  Varighed (timer)
                </span>
                <input type="number" min={1} value={job.durationHours} onChange={e => onUpdate({
              durationHours: parseFloat(e.target.value) || 1
            })} className={field} data-id="element-1626" data-mp="15q3xxkxr055k" />
              </label>
              <label className="block" data-id="element-1627" data-mp="1ga6b21e3lpm5">
                <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-1628" data-mp="1bc4nb35q924c">
                  Estimerede timer
                </span>
                <input type="number" min={0} value={job.estimatedHours} onChange={e => onUpdate({
              estimatedHours: parseFloat(e.target.value) || 0
            })} className={field} data-id="element-1629" data-mp="16fxi5cl5nung" />
              </label>
            </div>

            {/* How the job is billed. Regningsarbejde invoices what was
                actually used; a fixed price invoices the agreed sum. */}
            <div className="mt-4 border-t border-stone-100 pt-4" data-id="element-1630" data-mp="iqcwglxmajah">
              <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-1631" data-mp="dqgu0r4kizx1">
                Afregning
              </span>
              <div className="flex flex-wrap gap-2" data-id="element-1632" data-mp="3nbibyog4skv">
                {[{
              id: 'regning' as const,
              label: 'Regningsarbejde'
            }, {
              id: 'fast-pris' as const,
              label: 'Fast pris'
            }].map(option => <button key={option.id} onClick={() => onUpdate({
              billingType: option.id
            })} aria-pressed={billing === option.id} className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${billing === option.id ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1633" data-mp="iirgaes5d8mj">
                    {option.label}
                  </button>)}
              </div>

              {billing === 'fast-pris' && <label className="mt-3 block max-w-xs" data-id="element-1634" data-mp="336m7h5q72o7g">
                  <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-1635" data-mp="18rqgyl57jayr">
                    Aftalt pris ekskl. moms (kr)
                  </span>
                  <input type="number" min={0} value={job.fixedPrice ?? 0} onChange={e => onUpdate({
              fixedPrice: Math.max(0, parseFloat(e.target.value) || 0)
            })} className={field} data-id="element-1636" data-mp="13rkq376rhshq" />
                  <span className="mt-1 block text-xs text-stone-400" data-id="element-1637" data-mp="1njr07zj5cjzj">
                    Fakturaen oprettes med dette beløb, uanset forbrugt tid.
                  </span>
                </label>}
            </div>

            {billing === 'regning' && <div className="mt-4 border-t border-stone-100 pt-4" data-id="element-1638" data-mp="73ofciy09ref">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-id="element-1639" data-mp="24v12oaw9wd7">
                <label className="block" data-id="element-1640" data-mp="2drezudu5bsoc">
                  <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-1641" data-mp="2dsgd8oymwr98">
                    Timepris (kr)
                  </span>
                  <input type="number" min={0} step={5} value={rate} onChange={e => onUpdate({
                hourlyRate: Math.max(0, parseFloat(e.target.value) || 0)
              })} className={field} data-id="element-1642" data-mp="2ischmpb8ukpb" />
                </label>
                <div className="sm:col-span-2 sm:pt-7" data-id="element-1643" data-mp="1l39fhs7085p1">
                  <p className="text-sm text-stone-500" data-id="element-1644" data-mp="1l4kl7xk0fnis">
                    {usesOwnRate ? <>
                        Denne opgave bruger sin egen pris. Firmaets standard er{' '}
                        {companyHourlyRate.toLocaleString('da-DK')} kr/t.{' '}
                        <button onClick={() => onUpdate({
                    hourlyRate: null
                  })} className="font-medium text-brand-700 underline underline-offset-2 transition-colors hover:text-brand-800" data-id="element-1645" data-mp="3kfby707t0w1n">
                          Brug standardprisen
                        </button>
                      </> : <>
                        Følger firmaets standardpris. Ændrer du tallet, gælder
                        det kun denne opgave — og slår igennem på fakturaen.
                      </>}
                  </p>
                </div>
              </div>
            </div>}

            <fieldset className="mt-4" data-id="element-1646" data-mp="3rx9zwijqv2ub">
              <legend className="mb-2 text-sm font-medium text-stone-700" data-id="element-1647" data-mp="3nyb0tekp20as">
                Tildelte medarbejdere
              </legend>
              <div className="flex flex-wrap gap-2" data-id="element-1648" data-mp="obukmmqraspu">
                {employees.map(employee => {
              const isOn = job.employeeIds.includes(employee.id ?? '');
              return <button key={employee.id} onClick={() => onUpdate({
                employeeIds: isOn ? job.employeeIds.filter(id => id !== employee.id) : [...job.employeeIds, employee.id ?? '']
              })} aria-pressed={isOn} className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${isOn ? 'border-brand-300 bg-brand-50 text-brand-700' : 'border-stone-200 text-stone-600 hover:bg-stone-50'}`} data-id="element-1649" data-mp="2285najql0rfg">
                      <span className="h-2 w-2 rounded-full" style={{
                  backgroundColor: employee.color
                }} data-id="element-1650" data-mp="38tyhcn2fitry" />
                      {employee.name}
                    </button>;
            })}
              </div>
            </fieldset>
          </section>
        </div>}

      {tab === 'timer' && <div className="space-y-5" data-id="element-1651" data-mp="2oq5rsbn9c141">
          <section className={`${card} p-5`} data-id="element-1652" data-mp="2or72ug49wnlr">
            <h2 className="mb-4 text-base font-semibold text-stone-900" data-id="element-1653" data-mp="2114fuef7u7u0">
              Registrer tid
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-4" data-id="element-1654" data-mp="1w2jl2uiqggcy">
              <select value={timeForm.employeeId} onChange={e => setTimeForm({
            ...timeForm,
            employeeId: e.target.value
          })} aria-label="Medarbejder" className={field} data-id="element-1655" data-mp="1w06zm7drnnv5">
                {employees.map(employee => <option key={employee.id} value={employee.id} data-id="element-1656" data-mp="1dcbqj0t3my8d">
                    {employee.name}
                  </option>)}
              </select>
              <select value={timeForm.workType} onChange={e => setTimeForm({
            ...timeForm,
            workType: e.target.value as WorkType
          })} aria-label="Arbejdstype" className={field} data-id="element-1657" data-mp="17ybnpqfj30qe">
                {WORK_TYPES.map(type => <option key={type} value={type} data-id="element-1658" data-mp="1lw8jikm5r340">
                    {type}
                  </option>)}
              </select>
              <input value={timeForm.hours} onChange={e => setTimeForm({
            ...timeForm,
            hours: e.target.value
          })} placeholder="Timer, f.eks. 2,5" inputMode="decimal" aria-label="Timer" className={field} data-id="element-1659" data-mp="1byvmeyfr2cen" />
              <button onClick={addTime} className="rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-1660" data-mp="5hgvejw1oi0i">
                Tilføj
              </button>
            </div>
            <input value={timeForm.note} onChange={e => setTimeForm({
          ...timeForm,
          note: e.target.value
        })} placeholder="Note (valgfri)" className={`${field} mt-3`} data-id="element-1661" data-mp="2sjazk1qf0kls" />
          </section>

          <section className={`${card} overflow-hidden`} data-id="element-1662" data-mp="2nlhw3gghy4p7">
            <header className="flex items-center justify-between gap-3 border-b border-stone-100 px-5 py-3" data-id="element-1663" data-mp="29tl9tt1iep0q">
              <h2 className="text-base font-semibold text-stone-900" data-id="element-1664" data-mp="2duexclb1i1t3">
                Registreret på opgaven
              </h2>
              <span className="text-sm font-semibold tabular-nums text-stone-900" data-id="element-1665" data-mp="1zwghtckzfzc0">
                {registered.toLocaleString('da-DK')} t
              </span>
            </header>
            <TimeEntryList entries={own} emptyTitle="Ingen timer registreret" onUpdate={onUpdateTime} onRemove={onRemoveTime} data-id="element-1666" data-mp="1l7na4glsu0zj" />
          </section>
        </div>}

      {tab === 'materialer' && <div className="space-y-5" data-id="element-1667" data-mp="1q7je4fuhxsf3">
          <section className={`${card} p-5`} data-id="element-1668" data-mp="1q6hzy2i6opt5">
            <h2 className="mb-4 text-base font-semibold text-stone-900" data-id="element-1669" data-mp="1q9w51ii8fd4a">
              Tilføj fra kartotek
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_120px_auto]" data-id="element-1670" data-mp="1z8hmfabywm3h">
              <select value={materialPick} onChange={e => setMaterialPick(e.target.value)} aria-label="Materiale" className={field} data-id="element-1671" data-mp="2dx48duw3oc3o">
                <option value="" data-id="element-1672" data-mp="2mxiu2vcz7t7p">Vælg materiale…</option>
                {materials.map(material => <option key={material.id} value={material.id} data-id="element-1673" data-mp="2iwqkg106zmaw">
                    {material.name} — {kr(material.salesPrice)}/{material.unit}
                  </option>)}
              </select>
              <input value={materialQty} onChange={e => setMaterialQty(e.target.value)} inputMode="decimal" aria-label="Antal" className={field} data-id="element-1674" data-mp="2uxotlgg9filv" />
              <button onClick={addMaterial} className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-1675" data-mp="34qe4oa7weugo">
                <PlusIcon className="h-4 w-4" data-id="element-1676" data-mp="3ip43buad9275" />
                Tilføj
              </button>
            </div>
          </section>

          <section className={`${card} overflow-hidden`} data-id="element-1677" data-mp="3nme05v034g50">
            {job.materials.length === 0 ? <EmptyState icon={PackageIcon} title="Ingen materialer endnu" data-id="element-1678" data-mp="1obw6jx2tygiv" /> : <>
                <ul className="divide-y divide-stone-100" data-id="element-1679" data-mp="1taz8h8nik6mm">
                  {job.materials.map(item => <li key={item.id} className="flex items-center gap-4 px-5 py-3" data-id="element-1680" data-mp="5nbf4ndmlnsk">
                      <span className="min-w-0 flex-1" data-id="element-1681" data-mp="1mhnbix2boue">
                        <span className="block truncate text-sm font-medium text-stone-900" data-id="element-1682" data-mp="3ss9f0mqhn9jb">
                          {item.name}
                        </span>
                        <span className="block font-mono text-xs text-stone-400" data-id="element-1683" data-mp="tc2xkn1eyrs5">
                          {item.itemNo}
                        </span>
                      </span>
                      <span className="text-sm tabular-nums text-stone-600" data-id="element-1684" data-mp="kamocimc4vpi">
                        {item.quantity} × {kr(item.salesPrice)}
                      </span>
                      <span className="w-24 text-right text-sm font-semibold tabular-nums text-stone-900" data-id="element-1685" data-mp="352otbw2y9y6c">
                        {kr(item.quantity * item.salesPrice)}
                      </span>
                      <button onClick={() => onUpdate({
                materials: job.materials.filter(m => m.id !== item.id)
              })} aria-label={`Fjern ${item.name}`} className="rounded-lg p-1.5 text-stone-300 transition-colors hover:bg-red-50 hover:text-red-600" data-id="element-1686" data-mp="rudtnzgvnqfm">
                        <Trash2Icon className="h-4 w-4" data-id="element-1687" data-mp="1fj6vgck6ksi5" />
                      </button>
                    </li>)}
                </ul>
                <div className="flex items-center justify-between border-t border-stone-200 bg-stone-50 px-5 py-3" data-id="element-1688" data-mp="11mt9aq4pc2xc">
                  <span className="text-sm font-medium text-stone-600" data-id="element-1689" data-mp="3maxstbb9o1r4">
                    Materialer i alt
                  </span>
                  <span className="text-sm font-bold tabular-nums text-stone-900" data-id="element-1690" data-mp="3rak7zqanns2k">
                    {kr(materialTotal)}
                  </span>
                </div>
              </>}
          </section>
        </div>}

      {tab === 'billeder' && <section className={`${card} p-5`} data-id="element-1691" data-mp="3srg76aj2wxt9">
          <button onClick={() => onUpdate({
        photos: [...job.photos, {
          id: newLineId(),
          url: '',
          caption: 'Nyt billede',
          uploadedAt: new Date().toISOString()
        }]
      })} className="flex w-full flex-col items-center gap-2 rounded-xl border border-dashed border-stone-300 px-6 py-10 text-sm font-medium text-stone-500 transition-colors hover:border-brand-300 hover:text-brand-700" data-id="element-1692" data-mp="3sqel7b0iyqwr">
            <CameraIcon className="h-6 w-6" data-id="element-1693" data-mp="2h9erqc2fy615" />
            Tilføj billede fra kameraet
          </button>

          {job.photos.length > 0 && <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-1694" data-mp="gabhrpgop1ym">
              {job.photos.map(photo => <li key={photo.id} className="overflow-hidden rounded-lg border border-stone-200" data-id="element-1695" data-mp="l7bn1y5pm0oo">
                  <div className="flex aspect-square items-center justify-center bg-stone-100" data-id="element-1696" data-mp="uy6cfbe95gec">
                    <ImageIcon className="h-6 w-6 text-stone-300" data-id="element-1697" data-mp="3tg1wwgkhfqs8" />
                  </div>
                  <p className="truncate px-2 py-1.5 text-xs text-stone-600" data-id="element-1698" data-mp="29qzcueol85n">
                    {photo.caption}
                  </p>
                </li>)}
            </ul>}
        </section>}

      {tab === 'dokumenter' && <EmptyState icon={FileIcon} title="Ingen dokumenter" description="Tegninger og aftaler til denne opgave lægges her." data-id="element-1699" data-mp="bc093i24kxhz" />}

      {tab === 'noter' && <section className={`${card} p-5`} data-id="element-1700" data-mp="1mo12xbdujc4l">
          <label className="block" data-id="element-1701" data-mp="377ely1en6eyc">
            <span className="mb-2 block text-sm font-medium text-stone-700" data-id="element-1702" data-mp="376leey7uzlf5">
              Noter til opgaven
            </span>
            <textarea value={job.notes} onChange={e => onUpdate({
          notes: e.target.value
        })} rows={8} placeholder="Adgangsforhold, aftaler med kunden, hvad der mangler…" className={`${field} leading-relaxed`} data-id="element-1703" data-mp="336umusaid19k" />
          </label>
        </section>}

      {/* Desktop actions */}
      <div className="mt-6 hidden gap-3 lg:flex" data-id="element-1704" data-mp="2jh0plyp0xtxy">{actions}</div>

      {/* Phone action bar — thumb-sized, always reachable */}
      <div className="fixed inset-x-0 bottom-[52px] z-30 flex gap-2 border-t border-stone-200 bg-white/95 px-3 py-2.5 backdrop-blur lg:hidden" data-id="element-1705" data-mp="2fgqnix4crnod">
        {actions}
      </div>
    </div>;
}